import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { User, Plus, Settings, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import api from '../../services/api';
import { saveAttendance } from '../../utils/attendanceSave';
import ClearAttendanceButton from '../Attendance/ClearAttendanceButton';
import { useToastContext } from '../../context/ToastContext';
import { formatCurrency } from '../../utils/localeFormatters';
import './PrivateLessonsCard.css';

/**
 * The whole private-lesson day on the dashboard (docs/private-lessons-plan.md):
 * today's 1-on-1 lessons with one-tap attendance (which is what charges the
 * student and earns the teacher), past lessons still missing attendance, the
 * month so far with each teacher's pay, and — for managers — the price.
 * Nobody has to open the lessons, jibaya or student pages for any of it.
 */

const MARKS = [
  { status: 'present', key: 'privateLessons.present', fallback: 'Came' },
  { status: 'late', key: 'privateLessons.late', fallback: 'Late' },
  { status: 'absent', key: 'privateLessons.absent', fallback: 'Absent' },
];

export default function PrivateLessonsCard({ refreshKey = 0, onAdd, isManager = false }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { success, error: toastError } = useToastContext();
  const money = (v) => formatCurrency(v, i18n.language);

  const [data, setData] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(null); // lessonId being marked
  const [showPending, setShowPending] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [form, setForm] = useState({ price: '', unit: 'lesson', chargeAbsent: false });
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/learning/private-lessons/overview');
      const d = r?.today ? r : r?.data;
      setData(d || null);
      setLoadFailed(false);
      if (d?.settings) {
        setForm({
          price: d.settings.price === null || d.settings.price === undefined ? '' : String(d.settings.price),
          unit: d.settings.unit || 'lesson',
          chargeAbsent: !!d.settings.chargeAbsent,
        });
      }
    } catch {
      setLoadFailed(true);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const mark = async (lesson, status) => {
    setBusy(lesson.id);
    try {
      await saveAttendance({ lessonId: lesson.id, studentId: lesson.studentId, status });
      await load();
    } catch (err) {
      toastError(err?.message || t('common.error', { defaultValue: 'Something went wrong' }));
    } finally {
      setBusy(null);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/learning/private-lessons/settings', {
        price: form.price === '' ? null : Number(form.price),
        unit: form.unit,
        chargeAbsent: form.chargeAbsent,
      });
      success(t('privateLessons.settingsSaved', { defaultValue: 'Private-lesson price saved' }));
      setShowSettings(false);
      await load();
    } catch (err) {
      toastError(err?.message || t('common.error', { defaultValue: 'Something went wrong' }));
    } finally {
      setSavingSettings(false);
    }
  };

  const statusLabel = (s) => {
    const m = MARKS.find((x) => x.status === s);
    if (m) return t(m.key, { defaultValue: m.fallback });
    if (s === 'excused') return t('privateLessons.excused', { defaultValue: 'Excused' });
    return null;
  };

  const renderRow = (lesson, showDate = false) => {
    const done = !!lesson.status;
    return (
      <li key={lesson.id} className="pl-card__row" data-testid={`private-lesson-row-${lesson.id}`}>
        <div className="pl-card__when">
          {showDate && <span>{lesson.date.split('-').reverse().join('/')}</span>}
          <strong>{lesson.startTime || '—'}</strong>
        </div>
        <div className="pl-card__who">
          <button type="button" className="pl-card__student" onClick={() => navigate(`/students/${lesson.studentId}`)}>
            {lesson.studentName || t('privateLessons.unknownStudent', { defaultValue: 'Student' })}
          </button>
          <span className="pl-card__meta">
            {[lesson.teacherName, lesson.topic].filter(Boolean).join(' · ')}
          </span>
        </div>
        <div className="pl-card__money">
          {done
            ? (lesson.billedAmount > 0
              ? <span className="pl-card__billed">{money(lesson.billedAmount)}</span>
              : <span className="pl-card__free">{t('privateLessons.notCharged', { defaultValue: 'Not charged' })}</span>)
            : <span className="pl-card__price">{lesson.price > 0 ? money(lesson.price) : '—'}</span>}
        </div>
        <div className="pl-card__marks" role="group" aria-label={t('privateLessons.attendance', { defaultValue: 'Attendance' })}>
          {MARKS.map((m) => (
            <button
              key={m.status}
              type="button"
              disabled={busy === lesson.id}
              className={`pl-card__mark pl-card__mark--${m.status}${lesson.status === m.status ? ' is-on' : ''}`}
              aria-pressed={lesson.status === m.status}
              onClick={() => mark(lesson, m.status)}
              data-testid={`private-lesson-mark-${m.status}-${lesson.id}`}
            >
              {t(m.key, { defaultValue: m.fallback })}
            </button>
          ))}
          {done && (
            <ClearAttendanceButton
              status={lesson.status}
              onClear={() => mark(lesson, null)}
              disabled={busy === lesson.id}
              compact
              testId={`private-lesson-clear-${lesson.id}`}
            />
          )}
        </div>
        {done && !MARKS.some((m) => m.status === lesson.status) && (
          <span className="pl-card__meta">{statusLabel(lesson.status)}</span>
        )}
      </li>
    );
  };

  if (loadFailed && !data) return null;

  const today = data?.today || [];
  const pending = data?.pending || [];
  const month = data?.month;
  const noPrice = data && (data.settings?.price === null || data.settings?.price === undefined);

  return (
    <section className="pl-card" data-testid="private-lessons-card">
      <header className="pl-card__head">
        <h2><User size={18} /> {t('privateLessons.title', { defaultValue: 'Private lessons' })}</h2>
        <div className="pl-card__actions">
          {isManager && (
            <button type="button" className="btn btn-secondary pl-card__icon-btn" onClick={() => setShowSettings((v) => !v)}
              aria-label={t('privateLessons.priceSettings', { defaultValue: 'Private-lesson price' })} aria-expanded={showSettings}
              data-testid="private-lessons-settings-toggle">
              <Settings size={16} />
            </button>
          )}
          {onAdd && (
            <button type="button" className="btn btn-primary" onClick={onAdd} data-testid="private-lessons-add">
              <Plus size={16} /> {t('privateLessons.add', { defaultValue: 'Private lesson' })}
            </button>
          )}
        </div>
      </header>

      {isManager && noPrice && !showSettings && (
        <button type="button" className="pl-card__warn" onClick={() => setShowSettings(true)}>
          <AlertCircle size={16} /> {t('privateLessons.setPriceHint', { defaultValue: 'Set a private-lesson price so attendance can charge students automatically.' })}
        </button>
      )}

      {isManager && showSettings && (
        <div className="pl-card__settings" data-testid="private-lessons-settings">
          <label htmlFor="pl-price">
            <span>{t('privateLessons.defaultPrice', { defaultValue: 'Default price' })}</span>
            <input id="pl-price" type="number" min="0" inputMode="decimal" value={form.price}
              onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} />
          </label>
          <label htmlFor="pl-unit">
            <span>{t('privateLessons.per', { defaultValue: 'Per' })}</span>
            <select id="pl-unit" value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}>
              <option value="lesson">{t('privateLessons.perLesson', { defaultValue: 'Lesson' })}</option>
              <option value="hour">{t('privateLessons.perHour', { defaultValue: 'Hour' })}</option>
            </select>
          </label>
          <label htmlFor="pl-charge-absent" className="pl-card__check">
            <input id="pl-charge-absent" type="checkbox" checked={form.chargeAbsent}
              onChange={(e) => setForm((f) => ({ ...f, chargeAbsent: e.target.checked }))} />
            <span>{t('privateLessons.chargeAbsent', { defaultValue: 'Charge when the student is absent' })}</span>
          </label>
          <small>{t('privateLessons.settingsNote', { defaultValue: "A student's fixed price (set when scheduling) wins over this one." })}</small>
          <button type="button" className="btn btn-primary" onClick={saveSettings} disabled={savingSettings} data-testid="private-lessons-settings-save">
            {savingSettings ? t('common.saving', { defaultValue: 'Saving…' }) : t('common.save', { defaultValue: 'Save' })}
          </button>
        </div>
      )}

      <div className="pl-card__section">
        <h3>{t('privateLessons.today', { defaultValue: 'Today' })}</h3>
        {today.length === 0 ? (
          <p className="pl-card__empty">{t('privateLessons.noneToday', { defaultValue: 'No private lessons today.' })}</p>
        ) : (
          <ul className="pl-card__list">{today.map((l) => renderRow(l))}</ul>
        )}
      </div>

      {pending.length > 0 && (
        <div className="pl-card__section">
          <button type="button" className="pl-card__toggle pl-card__toggle--alert" onClick={() => setShowPending((v) => !v)} aria-expanded={showPending}>
            {t('privateLessons.pending', { defaultValue: '{{count}} past lessons still need attendance', count: pending.length })}
            {showPending ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {showPending && <ul className="pl-card__list">{pending.map((l) => renderRow(l, true))}</ul>}
        </div>
      )}

      {month && (
        <div className="pl-card__section">
          <div className="pl-card__stats">
            <div><span>{t('privateLessons.monthLessons', { defaultValue: 'Lessons this month' })}</span><strong>{month.lessons}</strong></div>
            <div><span>{t('privateLessons.monthTaught', { defaultValue: 'Taught' })}</span><strong>{month.taught}</strong></div>
            {isManager && <div><span>{t('privateLessons.monthRevenue', { defaultValue: 'Charged to students' })}</span><strong>{money(month.revenue)}</strong></div>}
            <div><span>{t('privateLessons.monthTeacherPay', { defaultValue: 'Teacher pay' })}</span><strong>{money(month.teacherPay)}</strong></div>
          </div>
          {month.byTeacher.length > 0 && (
            <>
              <button type="button" className="pl-card__toggle" onClick={() => setShowPay((v) => !v)} aria-expanded={showPay} data-testid="private-lessons-pay-toggle">
                {t('privateLessons.payByTeacher', { defaultValue: 'Pay per teacher this month' })}
                {showPay ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {showPay && (
                <div className="pl-card__table-wrap">
                  <table className="pl-card__table">
                    <thead>
                      <tr>
                        <th>{t('privateLessons.teacher', { defaultValue: 'Teacher' })}</th>
                        <th>{t('privateLessons.monthTaught', { defaultValue: 'Taught' })}</th>
                        <th>{t('privateLessons.hours', { defaultValue: 'Hours' })}</th>
                        <th>{t('privateLessons.pay', { defaultValue: 'Pay' })}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {month.byTeacher.map((row) => (
                        <tr key={row.teacherId || 'none'}>
                          <td>{row.teacherName || t('privateLessons.noTeacher', { defaultValue: '— No teacher —' })}</td>
                          <td>{row.taught} / {row.lessons}</td>
                          <td>{Math.round((row.minutes / 60) * 10) / 10}</td>
                          <td>
                            {money(row.pay)}
                            {row.teacherId && !row.hasRate && (
                              <span className="pl-card__norate">{t('privateLessons.noRate', { defaultValue: 'no hourly rate set' })}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
